import { useState, useRef, useEffect } from "react";
import axios from "axios";
import "./ChatBot.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const SUGGESTIONS = [
  "Explain my EBITDA margin",
  "What are my biggest risks?",
  "How healthy is my cash flow?"
];

export default function ChatBot({ analysisId, fileName }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Ask me anything about this balance sheet — margins, ratios, risk, whatever you need." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const question = text ?? input;
    if (!question.trim() || loading) return;

    if (!analysisId) {
      setMessages(prev => [...prev, { role: "user", content: question }, { role: "assistant", content: "No analysis is loaded yet — upload a file first, then ask me about it." }]);
      setInput("");
      return;
    }

    const userMsg = { role: "user", content: question };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/api/chat`, {
        analysis_id: analysisId,
        question,
        history: history.map(m => ({ role: m.role, content: m.content }))
      });
      setMessages([...history, { role: "assistant", content: res.data.answer }]);
    } catch (err) {
      const serverMsg = err?.response?.data?.detail;
      setMessages([...history, { role: "assistant", content: serverMsg || "Couldn't reach the server. Check your connection and try again." }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button className="chatbot-fab" onClick={() => setOpen(true)} aria-label="Open chatbot">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
      </button>
    );
  }

  return (
    <div className="chatbot-panel">
      <div className="chatbot-header">
        <div className="chatbot-header-left">
          <div className="chatbot-avatar-icon">✦</div>
          <div>
            <p className="chatbot-title">Ask about this report</p>
            <p className="chatbot-subtitle">{fileName || "Uploaded file"}</p>
          </div>
        </div>
        <button className="chatbot-close" onClick={() => setOpen(false)} aria-label="Close">✕</button>
      </div>

      <div className="chatbot-messages">
        {messages.map((m, i) => (
          <div key={i} className={`chatbot-row ${m.role === "user" ? "user" : ""}`}>
            {m.role === "assistant" && <div className="chatbot-avatar">✦</div>}
            <div className={`chatbot-bubble ${m.role}`}>{m.content}</div>
          </div>
        ))}
        {loading && (
          <div className="chatbot-row">
            <div className="chatbot-avatar">✦</div>
            <div className="chatbot-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="chatbot-footer">
        {messages.length < 2 && (
          <div className="chatbot-suggestions">
            {SUGGESTIONS.map((s, i) => (
              <button key={i} className="chatbot-chip" onClick={() => send(s)}>{s}</button>
            ))}
          </div>
        )}
        <div className="chatbot-input-row">
          <input
            type="text"
            value={input}
            placeholder="Ask a question about your data"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
          />
          <button className="chatbot-send" onClick={() => send()} aria-label="Send" disabled={loading}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}